// frontend/src/components/RouteGuards.js
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useUser } from '../UserContext';
import { CircularProgress, Box } from '@mui/material';
import { normalizeTierName, isConsultantTier } from '../utils/tierHelpers';

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #003366 0%, #004080 100%)'
      }}
    >
      <CircularProgress size={60} sx={{ color: '#FFD700', mb: 3 }} />
      <div style={{ color: '#fff', fontSize: '18px' }}>Loading your workspace...</div>
    </Box>
  );
}

/**
 * Guard for nonprofit-only routes
 * Redirects consultants to their dashboard
 */
export function NonprofitRoute({ children }) {
  const { orgProfile, profileLoading, authBlocked } = useUser();
  const { accounts } = useMsal();
  
  // Not authenticated
  if (!accounts || accounts.length === 0) {
    console.log('[NonprofitRoute] No authentication - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  if (authBlocked) {
    console.warn('[NonprofitRoute] Auth blocked - redirecting to login for explicit account selection');
    return <Navigate to="/login" replace />;
  }

  // Still loading profile
  if (profileLoading) {
    console.log('[NonprofitRoute] Waiting for profile to load...');
    return <LoadingSpinner />;
  }
  
  // No profile yet - should not happen if RedirectHandler works correctly
  if (!orgProfile) {
    console.error('[NonprofitRoute] No profile found - redirecting to redirect handler');
    return <Navigate to="/redirect" replace />;
  }
  
  // Check if consultant trying to access nonprofit route
  const normalizedTier = normalizeTierName(orgProfile.accountType);
  const isConsultant = isConsultantTier(normalizedTier);
  
  if (isConsultant) {
    console.log('[NonprofitRoute] Consultant accessing nonprofit route - redirecting to consultant dashboard');
    return <Navigate to="/consultant-dashboard" replace />;
  }
  
  // All checks passed - render nonprofit content
  return children;
}

/**
 * Guard for consultant-only routes
 * Redirects nonprofits to their dashboard
 */
export function ConsultantRoute({ children }) {
  const { orgProfile, profileLoading, authBlocked } = useUser();
  const { accounts } = useMsal();
  
  // Not authenticated
  if (!accounts || accounts.length === 0) {
    console.log('[ConsultantRoute] No authentication - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  if (authBlocked) {
    console.warn('[ConsultantRoute] Auth blocked - redirecting to login for explicit account selection');
    return <Navigate to="/login" replace />;
  }

  // Still loading profile OR authenticated but no profile yet (initial fetch)
  if (profileLoading || !orgProfile) {
    console.log('[ConsultantRoute] Waiting for profile to load...');
    return <LoadingSpinner />;
  }
  
  // Check if nonprofit trying to access consultant route
  const normalizedTier = normalizeTierName(orgProfile.accountType);
  const isConsultant = isConsultantTier(normalizedTier);
  
  if (!isConsultant) {
    console.log('[ConsultantRoute] Nonprofit accessing consultant route - redirecting to landing');
    return <Navigate to="/landing" replace />;
  }
  
  // All checks passed - render consultant content
  return children;
}

/**
 * Guard for routes that work for both types but require authentication
 * Examples: /grants, /account, /documents
 */
export function AuthenticatedRoute({ children }) {
  const { userProfile, profileLoading, authBlocked } = useUser();
  const { accounts } = useMsal();
  
  // Not authenticated
  if (!accounts || accounts.length === 0) {
    console.log('[AuthenticatedRoute] No authentication - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  if (authBlocked) {
    console.warn('[AuthenticatedRoute] Auth blocked - redirecting to login for explicit account selection');
    return <Navigate to="/login" replace />;
  }

  // Still loading profile or no profile yet (wait for UserContext)
  if (profileLoading || !userProfile) {
    console.log('[AuthenticatedRoute] Waiting for profile to load...');
    return <LoadingSpinner />;
  }
  
  // All checks passed - render content
  return children;
}
